export enum TypeEnum {
    FONT = "font",
    ARROWS = "arrows",
    BLOCK = "block",
    CONTENT = "content",
    PAGE = "page",
    SEPARATOR = "separator",
    UPLOAD = "upload",
    NAVIGATION = "navigation",
}

type Iri = string;

export type Uid = string;

export type Color = string;

export type Locale = string;

/**
 * Translatable text object with locale keys
 * @source database: qezako_story_page.top->contents[]->config.text
 * @example {"en": "Discover", "es": "Discover", "fr": "Découvrez"}
 */
export type TranslatableText = Record<Locale, string>;

/**
 * Font weight values for text content
 * @source view: ContentConfig.tsx, data.ts FONT_WEIGHTS
 */
export type FontWeight = "light" | "normal" | "semibold" | "bold";

/**
 * Flexbox alignment values
 * @source view: data.ts VFLEXS, HFLEXS, ARROWS
 */
export type FlexAlign = "flex-start" | "center" | "flex-end";

/**
 * Text alignment values
 * @source view: data.ts TEXT_ALIGNS
 */
export type TextAlign = "left" | "center" | "right" | null;

/**
 * Animation class names from animate.css
 * @source view: data.ts ANIMATIONS
 */
export type AnimationName =
    | ""
    | "bounceIn"
    | "bounceInRight"
    | "bounceInLeft"
    | "fadeIn"
    | "fadeInRight"
    | "fadeInLeft"
    | "zoomIn"
    | "zoomInRight"
    | "zoomInLeft"
    | "slideInRight"
    | "slideInLeft";

/**
 * Base config with timestamp - all configs extend this
 * @source database: all config JSON columns contain "_" timestamp
 */
type BaseConfig = {
    /** Last modification timestamp (ISO 8601 format) */
    _: DateIso;
};

/**
 * StoryPage config - background styling for the page
 * @source database: qezako_story_page.config
 * @source view: components/PageBlock.tsx
 * @example {"_": "2024-02-03T12:31:30+01:00", "backgroundColor": "#ed9907"}
 */
export type StoryPageConfig = BaseConfig & {
    /** Page background color (hex) */
    backgroundColor?: Color;
    /** Page background image */
    backgroundImage?: Upload;
    /** Backdrop blur filter in pixels (0 = no blur) */
    backdropFilter?: number;
};

/**
 * Block config - layout and background for content blocks
 * @source database: qezako_story_page.top, qezako_story_page.bottom
 * @source view: components/PageBlock.tsx
 * @example {"_": "...", "p": 4, "gap": 4, "alignItems": "center", "justifyContent": "center", "backgroundColor": null, "backgroundImage": {...}}
 */
export type BlockConfig = BaseConfig & {
    /** Padding (Bootstrap scale: 0-5) */
    p?: number;
    /** Gap between content items (Bootstrap scale: 0-5) */
    gap?: number;
    /** Vertical alignment of contents (flexbox) */
    alignItems?: FlexAlign;
    /** Horizontal alignment of contents (flexbox) */
    justifyContent?: FlexAlign;
    /** Block background color (hex) */
    backgroundColor?: Color | null;
    /** Block background image */
    backgroundImage?: Upload;
};

/**
 * Content config - styling for individual content items
 * Properties vary by ContentTypeEnum
 * @source database: qezako_story_page.top->contents[]->config
 * @source view: components/ContentConfig.tsx
 */
export type ContentConfig = BaseConfig & {
    /**
     * Translatable text content
     * @used_by TITLE, SUBTITLE, PARAGRAPH, LOCALE_PICKER
     * @source database: {"en": "...", "es": "...", "fr": "..."}
     */
    text?: TranslatableText;

    /**
     * Image upload data
     * @used_by IMAGE
     * @source database: {uri, path, mimeType, clientFilename}
     */
    image?: Upload;

    /**
     * Video upload data (can be YouTube or file)
     * @used_by VIDEO
     * @source database: {uri, path, mimeType, clientFilename, youtubeId?}
     */
    video?: Upload;

    /**
     * Audio source URL
     * @used_by AUDIO
     */
    src?: string | Upload;

    /**
     * Product identifier (SKU/EAN) for Origin'Info
     * @used_by ORIGIN
     */
    identifier?: string;

    /**
     * External link URL
     * @used_by TITLE, SUBTITLE, PARAGRAPH, LOCALE_PICKER (text types)
     * @source view: ContentConfig.tsx "Lien" field
     */
    href?: string;

    /**
     * Content height as percentage
     * @used_by IMAGE, VIDEO
     * @source view: ContentConfig.tsx, default: 20
     * @source database: stored as string "67" or number
     */
    height?: number | string;

    /**
     * Maximum width as percentage
     * @used_by IMAGE, VIDEO
     * @source view: ContentConfig.tsx, default: 50
     * @source database: stored as string "69" or number
     */
    maxWidth?: number | string;

    /**
     * Text/foreground color (hex)
     * @used_by TITLE, SUBTITLE, PARAGRAPH, LOCALE_PICKER
     * @source database: "#fffafa", "#ed9907"
     */
    color?: Color;

    /**
     * Background color (hex)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source database: "#d2c85b", "#ffffff"
     */
    backgroundColor?: Color;

    /**
     * Horizontal padding (Bootstrap scale: 0-5)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default 0
     * @source database: stored as string "3" or number
     */
    paddingX?: number | string;

    /**
     * Vertical padding (Bootstrap scale: 0-5)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default 0
     * @source database: stored as string "2" or number
     */
    paddingY?: number | string;

    /**
     * Rotation angle in degrees (-45 to 45)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default 0
     * @source database: stored as string "-15" or number -7
     */
    angle?: number | string;

    /**
     * Border radius in rem (0-3)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default 0
     * @source database: stored as string "0.8" or number 1
     */
    borderRadius?: number | string;

    /**
     * Text alignment
     * @used_by All text types
     * @source view: TEXT_ALIGNS - "left", "center", "right"; null follows the block alignment
     */
    textAlign?: TextAlign;

    /**
     * Custom font family name (from Story.fonts)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: ContentConfig.tsx font family selector
     */
    fontFamily?: string;

    /**
     * Font weight
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default "normal"
     */
    fontWeight?: FontWeight;

    /**
     * Font size as percentage (50-200)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: default 100
     * @source database: stored as string "90" or number
     */
    fontSize?: number | string;

    /**
     * Animation class name (animate.css)
     * @used_by TITLE, SUBTITLE, PARAGRAPH
     * @source view: ANIMATIONS list
     * @source database: "zoomInLeft", "bounceInRight", "fadeIn", etc.
     */
    animation?: AnimationName | string;

    /**
     * Animation start delay in milliseconds
     * @used_by TITLE, SUBTITLE, PARAGRAPH (when animation is set)
     * @source view: default 0, max 5000
     * @source database: stored as string "0" or number
     */
    animationDelay?: number | string;

    /**
     * Animation duration in milliseconds
     * @used_by TITLE, SUBTITLE, PARAGRAPH (when animation is set)
     * @source view: default 500, range 500-5000
     * @source database: stored as string "1500" or number 1500
     */
    animationDuration?: number | string;
};

/**
 * Arrows config - navigation arrow styling
 * @source database: qezako_story_page.arrows
 * @source view: components/ui/UiArrows.tsx
 * @example {"_": "...", "alignItems": "center", "color": null}
 */
export type ArrowsConfig = BaseConfig & {
    /** Vertical alignment of arrows */
    alignItems?: FlexAlign;
    /** Arrow icon color (hex) */
    color?: Color | null;
    /** Arrow background color (hex) */
    backgroundColor?: Color;
};

/**
 * Separator config - visual separator between blocks
 * @source view: components/ui/UiSeparator.tsx
 * @source database: qezako_story_page.top->separator (usually null)
 */
export type SeparatorConfig = BaseConfig & {
    /** Separator background/decoration image */
    backgroundImage?: Upload;
    /** Flex basis as percentage (default: 30) */
    flexBasis?: number;
};

/**
 * Navigation config - currently minimal, uses arrows
 * @source database: qezako_story_page.navigation (usually null)
 */
export type NavigationConfig = BaseConfig;

/**
 * Font config - no additional config properties
 * Font uses regular type properties (family, font, format, weights)
 * @source view: Font type definition
 */
export type FontConfig = BaseConfig;

/**
 * Generic Config type for backward compatibility
 * Prefer using specific config types (StoryPageConfig, BlockConfig, etc.)
 */
export type Config = BaseConfig & Record<string, unknown>;

/**
 * Union of every config property read by TemplatingUtils.style()
 * Any concrete config (ContentConfig, BlockConfig, StoryPageConfig,
 * SeparatorConfig, ArrowsConfig) is assignable to it.
 */
export type StyleConfig = {
    _?: DateIso;
    // text
    color?: Color | null;
    fontSize?: number | string | null;
    textAlign?: TextAlign;
    fontFamily?: string | null;
    // shape
    height?: number | string | null;
    maxWidth?: number | string | null;
    borderRadius?: number | string | null;
    // background
    backgroundColor?: Color | null;
    backgroundImage?: Upload | null;
    // flex
    flexBasis?: number | string | null;
    justifyContent?: FlexAlign;
    alignItems?: FlexAlign;
    // backdrop
    backdropFilter?: number | string | null;
    // animation
    angle?: number | string | null;
    animationDelay?: number | string | null;
    animationDuration?: number | string | null;
};

type UiTrait<C extends BaseConfig = Config> = {
    uid: Uid;
    config: C;
}

export type Entity<N> = {
    readonly "@id": Iri;
    readonly "@type": N;
    readonly id: number;
    readonly createdAt: DateIso;
    readonly updatedAt: DateIso;
}

export type Story = Entity<"Story"> & {
    name: string;
    slug: string;
    locales: Locale[];
    fonts: Font[];
    publishedAt: DateIso | null;
    company: Iri;
    config: {
        width: number;
        height: number;
    }
};

export enum FontFormatEnum {
    WOFF = "woff",
    WOFF2 = "woff2",
}

export const FontWeightEnum = [
    300,
    400,
    500,
    600,
    700,
];

export type Font = UiTrait<FontConfig> & {
    readonly "#type": TypeEnum.FONT;
    family: string;
    font: Upload | null;
    format: FontFormatEnum;
    weights: number[];
    idx: number; // @todo false
};

export type Upload = {
    readonly "#type": TypeEnum.UPLOAD;
    path: string;
    uri: string;
    clientFilename: string;
    mimeType: string | null;
    youtubeId?: string | null;
}

export type StoryItem = Entity<"StoryItem"> & {
    owner: Story["@id"];
    story: Story["@id"] | null;
    page: StoryPage["@id"] | null;
    idx: number;
}

export type StoryPage = Entity<"StoryPage"> & UiTrait<StoryPageConfig> & {
    readonly "#type": TypeEnum.PAGE;
    name: string;
    navigation: Navigation | null;
    arrows: Arrows | null;
    top: Block;
    bottom: Block | null;
    from: DateIso | null;
    to: DateIso | null;
    company: Iri;
    readonly isOnline: boolean;
};

export type DateIso = string;

export type Navigation = UiTrait<NavigationConfig> & {
    readonly "#type": TypeEnum.NAVIGATION;
    arrows: Arrows;
}

export type Arrows = UiTrait<ArrowsConfig> & {
    readonly "#type": TypeEnum.ARROWS;
}

export type Block = UiTrait<BlockConfig> & {
    readonly "#type": TypeEnum.BLOCK;
    contents: Content[];
    separator: Separator | null;
};

export type Separator = UiTrait<SeparatorConfig> & {
    readonly "#type": TypeEnum.SEPARATOR;
};

export enum ContentTypeEnum {
    TITLE = "title",
    SUBTITLE = "subtitle",
    PARAGRAPH = "paragraph",
    IMAGE = "image",
    VIDEO = "video",
    AUDIO = "audio",
    ORIGIN = "origin",
    LOCALE_PICKER = "locale_picker",
}

export type Content = UiTrait<ContentConfig> & {
    readonly "#type": TypeEnum.CONTENT;
    type: ContentTypeEnum;
};

export type ContentType = {
    value: ContentTypeEnum;
    label: string;
    placeholder: string;
    icon: string;
    toText: (content: Content, locale: Locale) => ContentInfos["text"];
    field: "text" | "image" | "video" | "src" | "identifier";
};

export type ContentInfos = {
    label: ContentType["label"];
    placeholder: ContentType["placeholder"];
    icon: ContentType["icon"];
    field: ContentType["field"];
    text: string;
};

export type JsonLdResponse<T> = {
    member: T[];
    totalItems?: number;
    view?: {
        previous?: string;
        next?: string;
    }
}

export type Route = {
    id: string;
    item: StoryItem;
    page: StoryPage;
    story: Story | null;
}

export type Nav = {
    item: StoryItem;
    story: Story | null;
    routes: Route[];
}
